import { useEffect, useState } from "react";
import { Typography } from "antd";
import streenTheme from "../css/theme.scss";
import useReachySpeech from "./useReachySpeech";

const { Text } = Typography;

const sanitizeTtsText = (text = "") =>
  String(text).replace(/<\/?p[^>]*>/g, "");

const isIpadBrowser = () => {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent || "";
  const isIpadUA = /iPad/i.test(ua);
  const isTouchMac =
    /Macintosh/i.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;

  return isIpadUA || isTouchMac;
};

const isAnnaVoice = (voice) =>
  /\banna\b/i.test(voice?.name || "") || /\banna\b/i.test(voice?.voiceURI || "");

const getWordIndexFromCharIndex = (text, charIndex) => {
  if (charIndex == null || !text) return null;

  const words = text.split(" ");
  let cursor = 0;

  for (let i = 0; i < words.length; i++) {
    const word = words[i];

    if (!word) {
      cursor += 1;
      continue;
    }

    const start = cursor;
    const end = start + word.length;

    if (charIndex >= start && charIndex < end) {
      return i;
    }

    cursor = end + 1;
  }

  return null;
};

const getPreferredGermanVoice = () => {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    return null;
  }

  const voices = window.speechSynthesis.getVoices() || [];
  if (!voices.length) return null;

  const germanVoices = voices.filter((voice) => {
    const lang = (voice?.lang || "").toLowerCase();
    return lang === "de" || lang.startsWith("de-");
  });

  if (!germanVoices.length) return null;

  if (isIpadBrowser()) {
    const annaVoice = germanVoices.find((voice) => isAnnaVoice(voice));
    if (annaVoice) return annaVoice;
  }

  // exact de-DE voices first.
  return (
    germanVoices.find((voice) => voice.lang === "de-DE") ||
    germanVoices.find((voice) => voice.lang?.toLowerCase().startsWith("de-")) ||
    germanVoices.find((voice) => voice.lang?.toLowerCase() === "de") ||
    null
  );
};

const useReadAloud = () => {
  const [speaking, setSpeaking] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [preferredVoice, setPreferredVoice] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;

    const loadVoices = () => {
      setPreferredVoice(getPreferredGermanVoice());
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  const speak = (text) => {
    const utterance = new SpeechSynthesisUtterance(text);
    const germanVoice = preferredVoice || getPreferredGermanVoice();
    const ipad = isIpadBrowser();
    const usingAnna = isAnnaVoice(germanVoice);

    utterance.lang = "de-DE";
    if (germanVoice) {
      utterance.voice = germanVoice;
      utterance.lang = germanVoice.lang || "de-DE";
    }

    // Anna on iPad sounds less robotic with slightly reduced rate and pitch.
    if (ipad && usingAnna) {
      utterance.rate = 0.87;
      utterance.pitch = 0.95;
    } else {
      utterance.rate = 1;
      utterance.pitch = 1;
    }
    utterance.volume = 1;

    utterance.onboundary = (event) => {
      if (event.name === "word") {
        setCurrentIndex(event.charIndex);
      }
    };

    utterance.onend = () => {
      setSpeaking(false);
      setCurrentIndex(null);
    };

    utterance.onerror = () => {
      setSpeaking(false);
      setCurrentIndex(null);
    };

    window.speechSynthesis.cancel();
    setSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  const stop = () => {
    window.speechSynthesis.cancel();
    setSpeaking(false);
    setCurrentIndex(null);
  };

  return {
    speak,
    stop,
    speaking,
    currentIndex,
  };
};

const useSentenceReadAloud = () => {
  const { speak, stop, speaking, currentIndex } = useReadAloud();
  const [activeSentenceId, setActiveSentenceId] = useState(null);

  /*const {
    speak: reachySpeak,
    loading,
    error,
  } = useReachySpeech();*/

  useEffect(() => {
    if (!speaking) {
      setActiveSentenceId(null);
    }
  }, [speaking]);

  const startSentence = (sentenceId, text) => {
    const cleanText = sanitizeTtsText(text);
    setActiveSentenceId(sentenceId);
    speak(cleanText);
     //reachySpeak(text);
  };

  const stopSentence = () => {
    stop();
    setActiveSentenceId(null);
  };

  const speakText = (text) => {
    setActiveSentenceId(null);
    speak(sanitizeTtsText(text));
   // reachySpeak(text);
  };

  const isSentenceSpeaking = (sentenceId) =>
    speaking && activeSentenceId === sentenceId;

  const getSentenceWordIndex = (sentenceId, text) => {
    if (!isSentenceSpeaking(sentenceId)) return null;

    const cleanText = sanitizeTtsText(text);
    return getWordIndexFromCharIndex(cleanText, currentIndex);
  };

  return {
    speaking,
    activeSentenceId,
    startSentence,
    stopSentence,
    speakText,
    isSentenceSpeaking,
    getSentenceWordIndex,
  };
};

const renderHighlightedText = (text, index) => {
  if (index == null) {
    return <Text>{text}</Text>;
  }

  // Find word start
  let start = index;
  while (start > 0 && /\S/.test(text[start - 1])) {
    start--;
  }

  // Find word end
  let end = index;
  while (end < text.length && /\S/.test(text[end])) {
    end++;
  }

  return (
    <Text>
      {text.slice(0, start)}
      <span
        style={{
          backgroundColor: streenTheme.orange4,
          padding: "0 2px",
          borderRadius: 3,
        }}
      >
        {text.slice(start, end)}
      </span>
      {text.slice(end)}
    </Text>
  );
};

export {
  getWordIndexFromCharIndex,
  renderHighlightedText,
  sanitizeTtsText,
  useReadAloud,
  useSentenceReadAloud,
};
